import { memo, useEffect, useState } from 'react'
import { AnimatePresence, motion, MotionConfig, type Transition } from 'framer-motion'
import { Loadstone, type LoadstoneConfig } from '@highlight/ui'
import { getTurnStartTime } from '../../../../store'

// Thinking-diamond grid from the Loadstone studio. Fully CSS-driven — no
// main-thread animation work while tokens stream.
const chatLoaderConfig = {
  rows: 5,
  columns: 5,
  animation: 'pulse',
  duration: 900,
  stagger: 90,
  gap: 3,
  radius: 3,
  ledMode: false,
  cells: [
    [0, 2, 3],
    [1, 1, 2],
    [1, 2, 3],
    [1, 3, 4],
    [2, 0, 0],
    [2, 1, 1],
    [2, 3, 5],
    [2, 4, 5],
    [3, 1, 8],
    [3, 2, 7],
    [3, 3, 6],
    [4, 2, 7],
  ].map(([row, column, order]) => ({
    row,
    column,
    order,
    lightColor: '#7c5cff',
    darkColor: '#7c5cff',
    opacity: 0.18,
    scale: 0.45,
  })),
} satisfies LoadstoneConfig

// Exit on opacity only — never height/layout, which would run layout animation
// against the streaming content below.
const loaderTransition: Transition = {
  opacity: { duration: 0.25, ease: 'easeOut' },
}

// ---------------------------------------------------------------------------
// ElapsedTime — the 10 Hz timer is quarantined here so only this span
// re-renders per tick, not the whole assistant content renderer.
// ---------------------------------------------------------------------------

function formatElapsed(ms: number): string {
  const s = ms / 1000
  if (s < 60) return `${s.toFixed(1)}s`
  const m = Math.floor(s / 60)
  const rem = Math.floor(s % 60)
  return `${m}m ${rem}s`
}

function ElapsedTime({ conversationId }: { conversationId: string }) {
  const [elapsed, setElapsed] = useState('0.0s')

  useEffect(() => {
    // Persisted start time so the timer survives drawer close/reopen
    const start = getTurnStartTime(conversationId) ?? Date.now()
    const id = setInterval(() => setElapsed(formatElapsed(Date.now() - start)), 100)
    return () => clearInterval(id)
  }, [conversationId])

  return <span className="text-foreground-placeholder text-[13px] tabular-nums leading-4">{elapsed}</span>
}

// ---------------------------------------------------------------------------
// StreamingLoader
// ---------------------------------------------------------------------------

interface StreamingLoaderProps {
  show: boolean
  conversationId: string
  /** Accessible label; the resume path overrides the default. */
  label?: string
}

export const StreamingLoader = memo(({ show, conversationId, label = 'Responding' }: StreamingLoaderProps) => (
  <MotionConfig reducedMotion="user">
    <AnimatePresence initial={false}>
      {show && (
        <motion.div
          key="streaming-loader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={loaderTransition}
          // Purely presentational — never eat clicks meant for the content it
          // sits beside (or fades out over).
          className="pointer-events-none flex items-center gap-2 py-1.5"
        >
          <Loadstone config={chatLoaderConfig} size={20} label={label} />
          <ElapsedTime conversationId={conversationId} />
        </motion.div>
      )}
    </AnimatePresence>
  </MotionConfig>
))
StreamingLoader.displayName = 'StreamingLoader'
